import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import type { AdminTab } from '../AdminShell';

type Counts = {
  products: number;
  customers_active: number;
  customers_prospect: number;
  deals_open: number;
  quotes_pending: number;
  orders_to_ship: number;
  orders_pending_payment: number;
  inventory_low: number;
  inventory_out: number;
};

export function AdminDashboard({ onTab }: { onTab: (t: AdminTab) => void }) {
  const [c, setC] = useState<Counts | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [
        prods, custActive, custProspect,
        dealsOpen, quotesPending, ordersToShip, ordersUnpaid,
        invLow, invOut,
      ] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('status', 'lead'),
        supabase.from('deals').select('*', { count: 'exact', head: true }).eq('status', 'open'),
        supabase.from('quotes').select('*', { count: 'exact', head: true }).eq('kind', 'to_nuled').in('status', ['sent', 'viewed']),
        supabase.from('orders').select('*', { count: 'exact', head: true }).in('status', ['confirmed', 'in_production', 'ready_to_ship']),
        supabase.from('orders').select('*', { count: 'exact', head: true }).in('payment_status', ['pending', 'partial']),
        supabase.from('inventory').select('*', { count: 'exact', head: true }).gt('reorder_point', 0).lte('on_hand', 5),
        supabase.from('inventory').select('*', { count: 'exact', head: true }).eq('on_hand', 0),
      ]);
      const error = prods.error ?? custActive.error ?? custProspect.error
        ?? dealsOpen.error ?? quotesPending.error ?? ordersToShip.error
        ?? ordersUnpaid.error ?? invLow.error ?? invOut.error;
      if (error) { setErr(error.message); return; }
      setC({
        products: prods.count ?? 0,
        customers_active: custActive.count ?? 0,
        customers_prospect: custProspect.count ?? 0,
        deals_open: dealsOpen.count ?? 0,
        quotes_pending: quotesPending.count ?? 0,
        orders_to_ship: ordersToShip.count ?? 0,
        orders_pending_payment: ordersUnpaid.count ?? 0,
        inventory_low: invLow.count ?? 0,
        inventory_out: invOut.count ?? 0,
      });
    })();
  }, []);

  return (
    <AdminContainer title="Dashboard" eyebrow="Operación NULED">
      {err && <Banner color={theme.err}>Error: {err}</Banner>}

      {/* Acciones urgentes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <Action urgent onClick={() => onTab('cotizaciones')}
          label="Cotizaciones pendientes" value={c?.quotes_pending}
          caption="Esperan tu respuesta" />
        <Action urgent onClick={() => onTab('pedidos')}
          label="Pedidos por enviar" value={c?.orders_to_ship}
          caption="Confirmados / en producción / listos" />
        <Action urgent onClick={() => onTab('pagos')}
          label="Pagos pendientes" value={c?.orders_pending_payment}
          caption="Cobranza activa" />
        <Action urgent onClick={() => onTab('clientes')}
          label="Aplicaciones distribuidor" value={c?.customers_prospect}
          caption="Por aprobar / rechazar" />
      </div>

      {/* Inventario crítico */}
      <div style={{
        background: '#FFF8E5',
        border: `1px solid #F2D78F`,
        borderLeft: `4px solid ${theme.warn}`,
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ ...eyebrow, color: '#A06800' }}>⚠ Inventario crítico</div>
            <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginTop: 6, fontWeight: 500 }}>
              <strong>{c?.inventory_out ?? '—'}</strong> SKUs sin stock · <strong>{c?.inventory_low ?? '—'}</strong> con stock bajo
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
              Revisa sugerencias de reposición y genera órdenes de compra.
            </div>
          </div>
          <button onClick={() => onTab('inventario')} style={btnPrimary}>
            Ver sugerencias
          </button>
        </div>
      </div>

      {/* Stats negocio */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 24,
      }}>
        <Stat label="Productos activos"      value={c?.products} />
        <Stat label="Distribuidores activos"  value={c?.customers_active} highlight />
        <Stat label="Deals abiertos"          value={c?.deals_open} />
      </div>

      {/* Atajos */}
      <div style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        padding: 20,
      }}>
        <div style={eyebrow}>Atajos</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          <Quick onClick={() => onTab('productos')}>+ Nuevo producto</Quick>
          <Quick onClick={() => onTab('compras')}>+ Orden de compra</Quick>
          <Quick onClick={() => onTab('clientes')}>+ Alta de distribuidor</Quick>
          <Quick onClick={() => onTab('cotizaciones')}>📥 Bandeja cotizaciones</Quick>
          <Quick onClick={() => onTab('reportes')}>📈 Ver reportes</Quick>
        </div>
      </div>
    </AdminContainer>
  );
}

function Action({ label, value, caption, urgent, onClick }: {
  label: string; value: number | undefined; caption: string; urgent?: boolean; onClick: () => void;
}) {
  const hasValue = value != null && value > 0;
  return (
    <button onClick={onClick} style={{
      background: theme.bg,
      border: `1px solid ${urgent && hasValue ? theme.warn : theme.border}`,
      borderLeft: urgent && hasValue ? `4px solid ${theme.warn}` : undefined,
      padding: 18,
      cursor: 'pointer',
      textAlign: 'left',
      fontFamily: theme.font.sans,
    }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{
        fontFamily: theme.font.head,
        fontSize: 26,
        fontWeight: 700,
        color: hasValue ? theme.brand : theme.textMuted,
        marginTop: 6,
      }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
        {caption}
      </div>
    </button>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number | undefined; highlight?: boolean }) {
  return (
    <div style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      borderTop: highlight ? `3px solid ${theme.accent}` : undefined,
      padding: 18,
    }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontFamily: theme.font.head, fontSize: 26, fontWeight: 600, color: theme.brand, marginTop: 6 }}>
        {value == null ? '—' : value.toLocaleString('es-MX')}
      </div>
    </div>
  );
}

function Quick({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      color: theme.brand,
      padding: '10px 16px',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function Banner({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div style={{
      padding: 12,
      background: theme.bg,
      border: `1px solid ${color}`,
      borderLeft: `4px solid ${color}`,
      color,
      marginBottom: 16,
      fontSize: 13,
    }}>
      {children}
    </div>
  );
}

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  color: theme.accentDark,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  fontWeight: 700,
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '12px 24px',
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};
